import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import {
  TaskStatusFormDto,
  type TaskStatusFormInterface
} from '@zaoblako/shared/modules/task/dtos/status/task-status-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { TaskStatusService } from '@zaoblako/web/core/services/task/task-status.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-payment-status-form',
  templateUrl: './payment-status-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    UserMultiSelectComponent,
    FormsModule,
    DividerModule,
    StatusLabelComponent,
    ButtonComponent,
    ColorPickerComponent,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentStatusFormComponent extends BaseFormComponent<TaskStatusFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public status = <TaskStatusFormInterface>{};
  public dto = TaskStatusFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly taskStatusService: TaskStatusService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.taskStatusService
      .findById<TaskStatusFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (status) => {
          this.status = status;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveStatus() {
    if (this.checkValidate(this.status).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.taskStatusService.update<TaskStatusFormInterface, TaskStatusFindInterface>(this.status).subscribe({
          next: (status) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Статус "${this.status.name}" обновлен`);
            this.formStoreService.saveNext(status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.taskStatusService.create<TaskStatusFindInterface>(this.status).subscribe({
          next: (status) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Статус создан');
            this.formStoreService.saveNext(status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteStatus() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить статус "${this.status.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.taskStatusService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Статус "${this.status.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeColor(color: string) {
    if (color) {
      this.status.color = color[0] !== '#' ? '#' + color : color;
    } else {
      this.status.color = '#ff0000';
    }
  }
}
